/* ─── View Manager - Handles view loading and switching ─── */
export class ViewManager {
  constructor() {
    this.views = new Map();
    this.currentView = null;
    this.viewCache = new Map();
    this.container = document.getElementById('app-shell') || document.body;
  }

  async loadView(name) {
    if (this.viewCache.has(name)) {
      return this.viewCache.get(name);
    }

    try {
      const response = await fetch(`/views/${name}.html`);
      if (!response.ok) throw new Error(`Failed to load view: ${name}`);
      const html = await response.text();
      this.viewCache.set(name, html);
      return html;
    } catch (error) {
      console.error(`Error loading view ${name}:`, error);
      return `<div class="view">Fehler beim Laden von ${name}: ${error.message}</div>`;
    }
  }

  async showView(name) {
    if (this.currentView === name) return;

    // Hide current view
    if (this.currentView) {
      const currentEl = document.getElementById(`view-${this.currentView}`);
      if (currentEl) currentEl.hidden = true;
    }

    // Load and show new view
    let viewEl = document.getElementById(`view-${name}`);
    if (!viewEl) {
      const html = await this.loadView(name);
      const temp = document.createElement('div');
      temp.innerHTML = html;
      viewEl = temp.firstElementChild;
      this.container.appendChild(viewEl);
    }

    viewEl.hidden = false;
    this.currentView = name;

    // Trigger view-specific initialization
    this.emitViewEvent(name, 'view:show');

    // Update URL hash
    if (window.location.hash !== `#${name}`) {
      history.replaceState(null, '', `#${name}`);
    }

    return viewEl;
  }

  emitViewEvent(viewName, eventName) {
    window.dispatchEvent(new CustomEvent(eventName, { detail: { view: viewName } }));
  }

  getCurrentView() {
    return this.currentView;
  }
}

/* ─── Component Base Class ─── */
export class Component {
  constructor(element) {
    this.element = element;
    this.cleanup = [];
  }

  on(event, selector, handler) {
    const listener = (e) => {
      const target = e.target.closest(selector);
      if (target && this.element.contains(target)) {
        handler.call(this, e, target);
      }
    };
    this.element.addEventListener(event, listener);
    this.cleanup.push(() => this.element.removeEventListener(event, listener));
    return listener;
  }

  destroy() {
    this.cleanup.forEach(fn => fn());
    this.cleanup = [];
  }
}

/* ─── Voice Orb Component ─── */
export class VoiceOrb extends Component {
  constructor(element) {
    super(element);
    this.state = 'idle'; // idle, listening, speaking, processing
    this.eqBars = element.querySelectorAll('.eq');
    this.transcript = element.querySelector('.transcript');
    this.bindEvents();
  }

  bindEvents() {
    this.on('click', '#orb-toggle', () => this.handleOrbClick());
    this.on('keydown', '#orb-toggle', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleOrbClick();
      }
    });
  }

  handleOrbClick() {
    if (this.state === 'idle') {
      this.startListening();
    } else if (this.state === 'listening') {
      this.stopListening();
    }
  }

  setState(state) {
    this.state = state;
    const stage = this.element.querySelector('.voice-stage');
    const orb = this.element.querySelector('.orb');

    stage.classList.remove('voice-state-idle', 'voice-state-listening', 'voice-state-speaking', 'voice-state-processing');
    stage.classList.add(`voice-state-${state}`);

    if (state === 'listening' || state === 'speaking') {
      orb.classList.add('active');
    } else {
      orb.classList.remove('active');
    }

    this.updateModeLabel(state);
  }

  updateModeLabel(state) {
    const label = this.element.querySelector('#mode-label');
    if (!label) return;

    const labels = {
      idle: 'BEREIT',
      listening: 'HÖRT ZU …',
      speaking: 'SPRICHT …',
      processing: 'VERARBEITET …'
    };
    label.textContent = labels[state] || 'BEREIT';
  }

  startListening() {
    this.setState('listening');
    this.emit('voice:start');
  }

  stopListening() {
    this.setState('processing');
    this.emit('voice:stop');
  }

  addMessage(role, content) {
    if (!this.transcript) return;

    const empty = this.transcript.querySelector('.empty-state');
    if (empty) empty.remove();

    const msg = document.createElement('div');
    msg.className = `message ${role}`;

    const label = document.createElement('strong');
    label.textContent = role === 'user' ? 'DU' : 'JARVIS';

    const text = document.createElement('span');
    text.textContent = content;

    msg.appendChild(label);
    msg.appendChild(text);
    this.transcript.appendChild(msg);
    this.transcript.scrollTop = this.transcript.scrollHeight;
  }

  clearTranscript() {
    if (!this.transcript) return;
    this.transcript.innerHTML = '<div class="empty-state"><span>◌</span><p>Noch keine Nachrichten. Starte ein Gespräch.</p></div>';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  emit(eventName, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

/* ─── Export all components ─── */
export const Components = {
  VoiceOrb,
  ViewManager,
  Component
};